import * as LDClientSdk from 'launchdarkly-js-client-sdk';
import type { LDClient } from 'launchdarkly-js-client-sdk';
import sdkPackageJson from 'launchdarkly-js-client-sdk/package.json';
import type {
  ClientConfig,
  LDService,
} from '@servicetitan/launchdarkly-service';

/**
 * Version of the `launchdarkly-js-client-sdk` bundled with this host.
 * `version` exists at runtime but the SDK typings do not declare it, so the
 * package.json version is the fallback if a future SDK release removes it.
 */
export const hostLDSdkVersion: string =
  (LDClientSdk as unknown as { version?: string }).version ??
  sdkPackageJson.version;

/**
 * The host `LDService` handed to embedded MFEs, plus a way to drop the host's client.
 * `getVersionClient` is required here, where the base type makes it optional, so
 * MFEs on a different build of the SDK can still share the host's client.
 */
export interface SharedLDService extends LDService {
  getVersionClient: NonNullable<LDService['getVersionClient']>;
  /**
   * Forgets the client the host SDK created for a client-side ID, so the next
   * `getClient` call builds a new one. Call it after closing that client.
   * @param clientSideID - the LaunchDarkly client-side ID the client was created for
   */
  release: (clientSideID: string) => void;
}

/**
 * Builds a host `LDService` that keeps one LaunchDarkly client per SDK version and
 * client-side ID. CCPro and every MFE that bundles the same SDK version read flags
 * through one client, so the browser opens one stream connection.
 * @param sdkVersion - version of the SDK this host bundles, used as the host's cache key
 * @returns a service to expose to MFEs through the web-components loader
 */
export function createSharedLDService(sdkVersion: string): SharedLDService {
  const clients = new Map<string, LDClient>();

  const toKey = (version: string, clientSideID: string) =>
    `${version}:${clientSideID}`;

  return {
    getClient: ({ clientSideID, context, options }: ClientConfig): LDClient => {
      const key = toKey(sdkVersion, clientSideID);
      let client = clients.get(key);
      if (!client) {
        client = LDClientSdk.initialize(clientSideID, context, options);
        clients.set(key, client);
      }
      return client;
    },

    getVersionClient: (version, config, createClient): LDClient => {
      const key = toKey(version, config.clientSideID);
      let client = clients.get(key);
      if (!client) {
        client = createClient(config);
        clients.set(key, client);
      }
      return client;
    },

    release: (clientSideID: string): void => {
      clients.delete(toKey(sdkVersion, clientSideID));
    },
  };
}

/**
 * The single host `LDService` for the app. `FeatureFlagModule` reads its client
 * from it, and the app root exposes it to MFE loaders.
 */
export const sharedLDService = createSharedLDService(hostLDSdkVersion);
